import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { casesAPI, adminAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Search, FileText, Plus, Eye } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

export default function AdminCases() {
  const [cases, setCases] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState({ userId: '', docType: 'CPF', docDigits: '' });
  const navigate = useNavigate();

  const fetchCases = async () => {
    setLoading(true);
    try {
      const res = await casesAPI.list({ page, search, limit: 20 });
      setCases(res.data.cases);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } catch (err) {
      toast.error('Erro ao carregar processos');
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const res = await adminAPI.listClients({ limit: 1000 });
      setClients(res.data.clients);
    } catch {}
  };

  useEffect(() => { fetchCases(); }, [page, search]);
  useEffect(() => { fetchClients(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await casesAPI.create(form);
      toast.success('Processo criado com sucesso!');
      setShowCreate(false);
      setForm({ userId: '', docType: 'CPF', docDigits: '' });
      fetchCases();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao criar processo');
    } finally {
      setCreating(false);
    }
  };

  const getProgressColor = (progress) => {
    if (progress >= 100) return 'text-[var(--rn-green)]';
    if (progress >= 50) return 'text-[var(--rn-warning)]';
    return 'text-white/70';
  };

  return (
    <div className="space-y-6" data-testid="admin-cases">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold">Processos</h1>
          <p className="text-sm text-white/50">{total} processos</p>
        </div>
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogTrigger asChild>
            <button className="btn-primary flex items-center gap-2" data-testid="admin-create-case-button">
              <Plus size={18} /> Novo Processo
            </button>
          </DialogTrigger>
          <DialogContent className="bg-[#111827] border-white/10 text-white">
            <DialogHeader>
              <DialogTitle className="font-display">Novo Processo</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 mt-4">
              <div>
                <label className="block text-sm text-white/70 mb-1.5">Cliente</label>
                <select value={form.userId} onChange={(e) => setForm(p => ({...p, userId: e.target.value}))} required data-testid="case-user-select" className="w-full bg-black/20 border border-white/10 text-white rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[rgba(0,255,163,0.30)]">
                  <option value="">Selecione um cliente</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.name} ({c.email})</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm text-white/70 mb-1.5">Tipo</label>
                <select value={form.docType} onChange={(e) => setForm(p => ({...p, docType: e.target.value}))} data-testid="case-doc-type-select" className="w-full bg-black/20 border border-white/10 text-white rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[rgba(0,255,163,0.30)]">
                  <option value="CPF">CPF</option>
                  <option value="CNPJ">CNPJ</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-white/70 mb-1.5">Numero do Documento</label>
                <input type="text" value={form.docDigits} onChange={(e) => setForm(p => ({...p, docDigits: e.target.value}))} required data-testid="case-doc-digits-input" className="w-full bg-black/20 border border-white/10 text-white placeholder:text-white/35 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[rgba(0,255,163,0.30)]" placeholder="Apenas numeros" />
              </div>
              <button type="submit" disabled={creating} className="w-full btn-primary py-3 disabled:opacity-50" data-testid="case-create-submit">
                {creating ? <span className="rn-spinner mx-auto" /> : 'Criar Processo'}
              </button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
        <input type="text" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="Buscar por CPF/CNPJ..." data-testid="admin-filters-search-input" className="w-full bg-black/20 border border-white/10 text-white placeholder:text-white/35 rounded-xl pl-11 pr-4 py-3 focus:outline-none focus:ring-2 focus:ring-[rgba(0,255,163,0.30)]" />
      </div>

      {/* Table */}
      <div className="rn-card overflow-x-auto">
        {loading ? (
          <div className="flex justify-center py-8"><div className="rn-spinner" style={{width:24,height:24}} /></div>
        ) : cases.length === 0 ? (
          <div className="text-center py-8" data-testid="empty-state">
            <FileText size={32} className="mx-auto text-white/20 mb-2" />
            <p className="text-sm text-white/40">Nenhum processo encontrado.</p>
          </div>
        ) : (
          <table className="w-full min-w-[800px]" data-testid="admin-cases-table">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left text-xs text-white/50 font-medium py-3 px-4">Cliente</th>
                <th className="text-left text-xs text-white/50 font-medium py-3 px-4">CPF/CNPJ</th>
                <th className="text-left text-xs text-white/50 font-medium py-3 px-4">Tipo</th>
                <th className="text-left text-xs text-white/50 font-medium py-3 px-4">Progresso</th>
                <th className="text-left text-xs text-white/50 font-medium py-3 px-4">Etapa</th>
                <th className="text-left text-xs text-white/50 font-medium py-3 px-4">Acoes</th>
              </tr>
            </thead>
            <tbody>
              {cases.map((c) => (
                <tr key={c.id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors" data-testid={`admin-cases-row-${c.id}`}>
                  <td className="py-3 px-4 text-sm">{c.user?.name || 'N/A'}</td>
                  <td className="py-3 px-4 text-sm font-mono text-white/80">{c.docFormatted}</td>
                  <td className="py-3 px-4"><span className="badge-neutral">{c.docType}</span></td>
                  <td className="py-3 px-4">
                    <span className={`font-display font-semibold ${getProgressColor(c.progress)}`}>{c.progress}%</span>
                  </td>
                  <td className="py-3 px-4 text-xs text-white/60">{getStageLabel(c.currentStage)}</td>
                  <td className="py-3 px-4">
                    <button onClick={() => navigate(`/admin/cases/${c.id}`)} className="p-2 rounded-lg hover:bg-white/5 transition-colors" data-testid={`view-case-${c.id}`}>
                      <Eye size={16} className="text-[var(--rn-green)]" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {pages > 1 && (
          <div className="flex items-center justify-between pt-4 border-t border-white/10 mt-4">
            <span className="text-xs text-white/40">Pagina {page} de {pages}</span>
            <div className="flex gap-2">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="btn-ghost text-xs px-3 py-1 disabled:opacity-30">Anterior</button>
              <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page === pages} className="btn-ghost text-xs px-3 py-1 disabled:opacity-30">Proximo</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function getStageLabel(stage) {
  const labels = {
    'REGISTERED': 'Cadastrado',
    'DOCUMENT_APPROVED': 'Doc. Aprovado',
    'TERM_SIGNED': 'Termo Assinado',
    'PROTOCOL_SENT': 'Protocolo Enviado',
    'ACTION_FILED': 'Acao Ajuizada',
    'ALL_CLEARED': 'Regularizado',
    'NONE': 'Iniciando',
  };
  return labels[stage] || stage;
}
